import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Header from './Header';
import Footer from './Footer';
import axiosInstance from '../api/axiosConfig';

const ProfileCompletion = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token } = useSelector(state => state.auth);
  
  const [formData, setFormData] = useState({
    dateOfBirth: '',
    gender: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');

  // Redirect if not authenticated
  useEffect(() => {
    if (!token) {
      navigate('/onboarding');
    }
  }, [token, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      if (age < 18) {
        newErrors.dateOfBirth = 'You must be at least 18 years old';
      } else if (age > 100) {
        newErrors.dateOfBirth = 'Please enter a valid date of birth';
      }
    }

    if (!formData.gender) {
      newErrors.gender = 'Please select your gender';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await axiosInstance.post('/auth/complete-profile', {
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
      });

      if (response.data?.success) {
        // Profile completed successfully
        navigate('/dashboard');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to complete profile';
      setGeneralError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    // Allow users to skip profile completion for now
    navigate('/dashboard');
  };

  return (
    <div className="bg-offwhite text-charcoal antialiased">
      <Header isOnboarding={true} />
      
      <div className="min-h-screen py-32 px-6 bg-gradient-to-b from-offwhite to-blush">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-4xl font-playfair font-bold text-charcoal mb-3">Complete Your Profile</h2>
                  <p className="text-charcoal/70">Help us get to know you better</p>
                </div>

                {generalError && (
                  <div className="bg-rose/10 border-2 border-rose/50 text-rose rounded-xl p-4">
                    {generalError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Date of Birth *</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    disabled={loading}
                    className={`w-full px-3 py-2 rounded-xl border-2 text-sm ${
                      errors.dateOfBirth ? 'border-rose' : 'border-charcoal/20'
                    } focus:outline-none focus:border-rose transition-colors disabled:opacity-50`}
                  />
                  {errors.dateOfBirth && <p className="text-rose text-xs mt-1">{errors.dateOfBirth}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Gender *</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    disabled={loading}
                    className={`w-full px-3 py-2 rounded-xl border-2 text-sm ${
                      errors.gender ? 'border-rose' : 'border-charcoal/20'
                    } focus:outline-none focus:border-rose transition-colors disabled:opacity-50`}
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                  {errors.gender && <p className="text-rose text-xs mt-1">{errors.gender}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-rose text-white py-3 rounded-xl font-semibold hover:bg-charcoal transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Completing Profile...' : 'Complete Profile'}
                </button>

                <button
                  type="button"
                  onClick={handleSkip}
                  disabled={loading}
                  className="w-full bg-charcoal/10 text-charcoal py-3 rounded-xl font-semibold hover:bg-charcoal/20 transition-colors duration-300 disabled:opacity-50"
                >
                  Skip for Now
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ProfileCompletion;
